import { HomeLoadingSkeleton } from '@components/Home/HomeLoadingSkeleton'
import { MEMBERS_AREA_FEATURED_PRODUCT } from '@constants/features'
import { useAdminPreview } from '@hooks/useAdminPreview'
import {
  isExpectedMembersConfigError,
  useMembersConfig,
} from '@hooks/useMembersConfig'
import { useThemeParams } from '@hooks/useThemeParams'
import { DefaultHomeScreen } from '@screens/DefaultHomeScreen'
import { FeaturedProductHome } from '@screens/FeaturedProductHome'
import * as Sentry from '@sentry/react-native'
import { useFocusEffect } from 'expo-router'
import { useCallback, useEffect, useRef, useState } from 'react'

type DecisionStage = 'initial' | 'refresh'

const reportDecisionFailure = (
  error: unknown,
  stage: DecisionStage,
  schoolId: number | string | undefined,
) => {
  Sentry.captureException(error, {
    tags: {
      type: 'home_decision_failure',
      stage,
      school_id: schoolId ? String(schoolId) : 'unknown',
    },
  })
}

/**
 * `/` decide entre a home padrão e a home focada no produto principal. Até a
 * configuração responder, mostra o mesmo esqueleto que a home padrão já mostra
 * nesse intervalo, para nunca piscar a home errada. Na dúvida, home padrão.
 */
export default function Home() {
  const { data, error, isResolved, refreshMembersConfig } = useMembersConfig()
  const { isPreview } = useAdminPreview()
  const {
    data: themeParams,
    loading: themeParamsLoading,
    error: themeParamsError,
  } = useThemeParams()
  const schoolIdRef = useRef(themeParams?.school?.id)
  schoolIdRef.current = themeParams?.school?.id
  const [isRefreshSettled, setIsRefreshSettled] = useState(false)

  useEffect(() => {
    if (error && !isExpectedMembersConfigError(error)) {
      reportDecisionFailure(error, 'initial', schoolIdRef.current)
    }
  }, [error])

  // A matrícula muda sem novo login (boleto/pix aprovado, acesso vencido), então
  // quem tem produto principal configurado reavalia a cada entrada em `/`. Sem
  // produto principal, nenhuma requisição a mais. Falha aqui mantém a decisão
  // que já estava em mãos.
  const hasFeaturedProduct = !!data?.featuredProduct
  useFocusEffect(
    useCallback(() => {
      if (!hasFeaturedProduct) return
      refreshMembersConfig()
        .catch((refreshError: unknown) =>
          reportDecisionFailure(refreshError, 'refresh', schoolIdRef.current),
        )
        .finally(() => setIsRefreshSettled(true))

      return () => setIsRefreshSettled(false)
    }, [hasFeaturedProduct, refreshMembersConfig]),
  )

  // O core só devolve `featuredProduct` com a feature ativa, pelo mesmo critério
  // de `featuresAvailable`, que o tema já carregou no boot. Sem ela, a home
  // padrão monta na hora e busca os próprios dados em paralelo com a config,
  // como antes da feature; esperar a config aqui atrasaria toda escola.
  const featuresAvailable = themeParams?.school?.featuresAvailable
  const schoolLacksFeature =
    !themeParamsLoading &&
    !themeParamsError &&
    !!featuresAvailable &&
    !featuresAvailable.includes(MEMBERS_AREA_FEATURED_PRODUCT)
  if (schoolLacksFeature) return <DefaultHomeScreen />

  if (error) return <DefaultHomeScreen />
  if (!isResolved) return <HomeLoadingSkeleton />

  // O colaborador não tem matrícula (`activeForUser: false`), mas no preview vê
  // a home focada sempre que o produto principal está publicado.
  const featuredProduct = data?.featuredProduct
  if (featuredProduct && (featuredProduct.activeForUser || isPreview)) {
    return <FeaturedProductHome featuredProduct={featuredProduct} />
  }

  // "Sem acesso" vindo do cache pode ser de antes do pagamento aprovado: a home
  // padrão montada agora apareceria e depois pularia para a focada.
  if (featuredProduct && !isRefreshSettled) return <HomeLoadingSkeleton />

  return <DefaultHomeScreen />
}
